import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useDepositAmountConverter } from '@/feature/devsettings/hooks/use-dev-settings';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { useWallet } from '@/hooks/use-wallet';
import { DepositScreenNavigationProp } from '@/navigation/deposit/types';
import { navigateToKyc } from '@/navigation/navigation';

import { PaymentMethodTypes } from '../const';
import {
    PaymentMethod,
    PreferredPayment,
    debitPaymentSelector,
    preferredPayPalPaymentSelector,
    useDepositPaymentMethods,
} from './use-deposit-payment-methods';
import { setDepositAmount, useDepositStatusStore } from './use-deposit-status-store';

type DepositButtonNavigationProps = {
    selectedPaymentMethod?: PaymentMethod;
    selectedPreferredMethod?: PreferredPayment;
    forceDepositWithDebitCard?: boolean;
};

/**
 * Custom hook for handling navigation based on the provided parameters when the deposit button is pressed.
 *
 * @returns A memoized callback function that executes the navigation logic based on the
 * provided parameters and the current state of payment methods.
 */
export const useDepositButtonNavigation = ({
    selectedPaymentMethod,
    selectedPreferredMethod,
    forceDepositWithDebitCard,
}: DepositButtonNavigationProps) => {
    const { t } = useTranslation('wallet');
    const tSocialWallet = useSocialWalletTranslation('wallet');
    const navigation = useNavigation<DepositScreenNavigationProp>();

    const { wallet } = useWallet();
    const { showInfoSheet } = useAlerts();

    const { data: paypalMethod } = useDepositPaymentMethods({ select: preferredPayPalPaymentSelector });
    const { data: debitMethod, isLoading: isInitialLoading } = useDepositPaymentMethods({
        select: debitPaymentSelector,
    });
    const { convertToMinorUnitAmount } = useDepositAmountConverter();

    return useCallback(() => {
        // read the amount on press, the deposit button sets it right before calling this
        const { selectedAmount, coinPackageId } = useDepositStatusStore.getState();
        const navigationParams = {
            selectedAmount,
            currency: wallet?.real_currency,
            payment_type_id: selectedPreferredMethod?.payment_type_id,
            coin_package_id: coinPackageId,
        };

        if (selectedPaymentMethod?.type === PaymentMethodTypes.PaysafeMazooma) {
            navigation.navigate('MazoomaDepositWebViewModal', navigationParams);
        } else if (selectedPaymentMethod?.type === PaymentMethodTypes.PaysafePaypal && !paypalMethod) {
            showInfoSheet({
                title: t('bank_requirement_modal_title'),
                description: t('bank_requirement_modal_subtitle'),
                buttonLabel: tSocialWallet('bank_requirement_modal_cta_label'),
                handlePress: () =>
                    navigation.navigate('DepositMethodWebViewModal', {
                        ...navigationParams,
                        selectedPaymentMethodType: selectedPaymentMethod?.type,
                    }),
            });
        } else if (forceDepositWithDebitCard && !isInitialLoading) {
            setDepositAmount(convertToMinorUnitAmount(selectedAmount));
            navigation.navigate('AddDebitCardWebView', {
                paysafeAccountId: debitMethod?.accountId ?? '',
                canUse3ds: debitMethod?.type === PaymentMethodTypes.Paysafe3ds,
            });
        } else if (selectedPaymentMethod?.type === PaymentMethodTypes.PaysafeVenmo) {
            navigation.navigate('VenmoDepositScreen', {});
        } else if (selectedPaymentMethod?.type === PaymentMethodTypes.FantasyMoney) {
            navigation.navigate('FantasyMoneyDepositScreen', {
                selectedPaymentMethodType: PaymentMethodTypes.FantasyMoney,
            });
        } else if (selectedPaymentMethod?.type === PaymentMethodTypes.SocialWallet) {
            navigation.navigate('SocialWalletDepositScreen', {
                selectedPaymentMethodType: PaymentMethodTypes.SocialWallet,
            });
        } else if (selectedPaymentMethod?.type === PaymentMethodTypes.Trustly) {
            if (
                selectedPreferredMethod?.should_reauthorize &&
                selectedPreferredMethod?.transaction_id &&
                selectedPreferredMethod?.payment_type_id
            ) {
                navigateToKyc(navigation, {
                    screen: 'TrustlyWebview',
                    params: {
                        transactionId: selectedPreferredMethod.transaction_id,
                        payment_type_id: selectedPreferredMethod.payment_type_id,
                    },
                });
            } else {
                navigation.navigate('TrustlyDepositScreen', navigationParams);
            }
        } else if (selectedPreferredMethod?.is_expired) {
            navigation.navigate('DepositExpiryUpdate', navigationParams);
        } else {
            if (!selectedPaymentMethod) {
                return;
            }
            navigation.navigate('DepositMethodWebViewModal', {
                ...navigationParams,
                selectedPaymentMethodType: selectedPaymentMethod?.type,
            });
        }
    }, [
        wallet?.real_currency,
        selectedPreferredMethod?.payment_type_id,
        selectedPreferredMethod?.is_expired,
        selectedPreferredMethod?.should_reauthorize,
        selectedPreferredMethod?.transaction_id,
        selectedPaymentMethod,
        paypalMethod,
        forceDepositWithDebitCard,
        navigation,
        showInfoSheet,
        t,
        tSocialWallet,
        convertToMinorUnitAmount,
        debitMethod?.accountId,
        debitMethod?.type,
        isInitialLoading,
    ]);
};
